import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  FileText,
  File,
  FileSpreadsheet,
  Presentation,
  Upload,
  Pencil,
  History,
  Eye,
  Download,
  CheckCircle,
  AlertTriangle,
  RotateCcw,
  Plus,
  Trash2,
  Code2,
  Sparkles,
  MoreVertical,
  ChevronUp,
  ChevronDown,
  Link2,
  ExternalLink,
  SmilePlus,
  HelpCircle,
  Copy,
  Check,
} from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import BackButton from '../BackButton';
import Modal from '../Modal';
import DocumentViewerModal from '../DocumentViewerModal';
import {
  fetchTemplatesGrouped,
  uploadNewVersion,
  updateTemplateDetails,
  fetchVersionHistory,
  rollbackToVersion,
  saveTailorHtml,
  saveTailorFormats,
  saveCanvaUrl,
  suggestNextVersion,
  getFileType,
} from '../../services/hrCollectionsService';
import { TAILOR_FORMATS, DEFAULT_TAILOR_FORMATS, normalizeFormatKeys } from '../../utils/tailorDownloads';
import { saveTemplateStructure, makeSectionId } from '../../services/hrStructureService';
import { getTemplateViewUrl } from '../../utils/hrTemplates';
import {
  TAILOR_TOKENS,
  KNOWN_TOKEN_KEYS,
  extractTokens,
  applyTokens,
  buildSampleValues,
  wrapTailoredHtml,
} from '../../utils/hrTailorTokens';
import PortalBackground from '../PortalBackground';
import BBALoader from '../BBALoader';
// Lazy-loaded so the full emoji picker (~large bundle) only downloads when opened.
const EmojiPicker = React.lazy(() => import('emoji-picker-react'));

const ALLOWED_EXT = /\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt)$/i;

const TypeIcon = ({ type, className = 'h-7 w-7' }) => {
  if (type === 'PDF') return <FileText className={`${className} text-red-500`} />;
  if (type === 'XLS') return <FileSpreadsheet className={`${className} text-green-600`} />;
  if (type === 'PPT') return <Presentation className={`${className} text-orange-500`} />;
  return <File className={`${className} text-blue-600`} />;
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

/**
 * The prompt a staff member pastes into Claude (alongside the latest template
 * file) to get tailoring HTML back. Built from TAILOR_TOKENS so the token list
 * in the prompt can never drift from what the tailoring engine supports.
 */
const buildClaudePrompt = (templateName = 'this template') => {
  const tokenLines = TAILOR_TOKENS.map((t) => {
    const hint = t.hint ? ` — ${t.hint}` : '';
    return `- {{${t.token}}} = ${t.label}${hint}`;
  }).join('\n');

  return `I'm attaching our latest "${templateName}" document. Convert it into a single HTML template that our client portal can personalize for each client company.

RULES FOR THE HTML
1. Return ONLY the HTML, in one code block. No explanation before or after it.
2. Output a body fragment only: do NOT include <!DOCTYPE>, <html>, <head>, <body>, <style> blocks, or <script> tags. The portal wraps your HTML in its own document shell.
3. Keep every heading, paragraph, list, table, and the wording exactly as it appears in the attached document. Do not summarize, shorten, or reword anything.
4. Use inline styles only (style="..."). Use semantic tags: <h1>–<h3>, <p>, <ul>/<ol>, <table>, <strong>, <em>. Keep it clean and printable on a letter-size page.
5. Do not use external fonts, external stylesheets, or images other than the logo token below.

TOKENS
Anywhere the document shows information specific to a company or person (company name, owner name, address, phone, email, website, logo, brand colors, date), replace that text with the matching token below, written exactly as shown with double curly braces. Use ONLY these tokens — do not invent new ones:

${tokenLines}

TOKEN USAGE NOTES
- Put the logo at the top as <img src="{{logoUrl}}" alt="{{companyName}} logo" style="max-height:80px">.
- Use the brand colors in style attributes, for example style="color:{{primaryColor}}" on headings or style="border-top:3px solid {{primaryColor}}" on dividers.
- If the document has a signature or date line, use {{ownerName}} and {{date}}.
- Where the document refers to "the Company", "our company", or the specific company name, use {{companyName}}.
- Leave placeholders meant for the client to fill in later (like an employee's name or a start date) as bracketed text, e.g. [Employee Name]. Do not turn those into tokens.

When you're done, double-check that every token you used is spelled exactly like the list above.`;
};

/**
 * Department-agnostic template collections manager (sections → template cards
 * with file versions + tailoring HTML). Config picks the department:
 * { title, pillar, structureId, defaultSections, backTo, accent: { tabActive,
 *   spinner, button }, sectionPlaceholder, itemPlaceholder, showCanvaLink }
 * `accent` values are full Tailwind class strings so the JIT compiler sees them.
 */
const TemplateCollections = ({ config }) => {
  const {
    title,
    pillar,
    structureId,
    defaultSections = [],
    backTo = '/crm',
    accent,
    sectionPlaceholder = 'e.g. Hiring Materials',
    itemPlaceholder = 'e.g. PTO Request Form',
    showCanvaLink = false,
  } = config;

  const { darkMode } = useTheme();
  const { currentUser } = useAuth();

  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(null);
  const [userName, setUserName] = useState('Unknown');

  // Section (tab) add/rename modal
  const [sectionModal, setSectionModal] = useState(null); // { mode: 'add' | 'rename', sectionId?, label }
  const [savingSection, setSavingSection] = useState(false);
  const [sectionError, setSectionError] = useState('');

  // Add-template modal
  const [itemModal, setItemModal] = useState(null); // { name, description }
  const [savingItem, setSavingItem] = useState(false);
  const [itemError, setItemError] = useState('');

  // Upload modal
  const [uploadEntry, setUploadEntry] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadVersion, setUploadVersion] = useState('');
  const [uploadNotes, setUploadNotes] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  // Edit modal
  const [editEntry, setEditEntry] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editSectionId, setEditSectionId] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  // History modal
  const [historyEntry, setHistoryEntry] = useState(null);
  const [versions, setVersions] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Card kebab menu (Edit details / Remove)
  const [menuKey, setMenuKey] = useState(null);

  // Tailor HTML modal
  const [htmlEntry, setHtmlEntry] = useState(null);
  const [htmlDraft, setHtmlDraft] = useState('');
  const [htmlSaving, setHtmlSaving] = useState(false);
  const [htmlError, setHtmlError] = useState('');
  const [htmlShowPreview, setHtmlShowPreview] = useState(false);
  // Card-level "Preview with sample data" — renders the saved tailoring HTML
  // with sample values, without opening the editor.
  const [previewEntry, setPreviewEntry] = useState(null);
  // "How to update" walkthrough modal + copied-state for its Claude prompt.
  const [howToEntry, setHowToEntry] = useState(null);
  const [promptCopied, setPromptCopied] = useState(false);

  const copyClaudePrompt = async () => {
    const text = buildClaudePrompt(howToEntry?.doc?.title || howToEntry?.name || howToEntry?.title);
    try {
      await navigator.clipboard.writeText(text);
      setPromptCopied(true);
      setTimeout(() => setPromptCopied(false), 2000);
    } catch (err) {
      console.error('Could not copy prompt:', err);
    }
  };
  const htmlTextareaRef = React.useRef(null);

  // Canva link modal
  const [canvaEntry, setCanvaEntry] = useState(null);
  const [canvaDraft, setCanvaDraft] = useState('');
  const [canvaSaving, setCanvaSaving] = useState(false);
  const [canvaError, setCanvaError] = useState('');

  // Emoji icon picker — targets a template ({ kind: 'item', entry }) or the
  // section being edited in the section modal ({ kind: 'section' })
  const [emojiTarget, setEmojiTarget] = useState(null);

  // Fullscreen in-page document viewer (replaces opening the file in a new tab).
  const [viewerDoc, setViewerDoc] = useState(null);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchTemplatesGrouped({ pillar, structureId, defaultSections });
      setSections(data);
      setActiveTab((current) =>
        data.some((s) => s.id === current) ? current : data[0]?.id || null
      );
    } catch (err) {
      console.error(`Error loading ${pillar} templates:`, err);
    } finally {
      setLoading(false);
    }
  }, [pillar, structureId, defaultSections]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // CRM sidebar search deep-links land here as ?material=<title> — activate
  // the tab holding that template, then scroll to and briefly highlight it.
  const [searchParams] = useSearchParams();
  const pendingMaterialRef = useRef(searchParams.get('material'));
  const [highlightTitle, setHighlightTitle] = useState(null);
  useEffect(() => {
    const material = pendingMaterialRef.current;
    if (!material || loading || !sections.length) return;
    const wanted = material.toLowerCase().trim();
    const matches = (i) =>
      [i.title, i.name, i.doc?.title].some(
        (v) => String(v || '').toLowerCase().trim() === wanted
      );
    const section = sections.find((s) => (s.items || []).some(matches));
    if (!section) return;
    pendingMaterialRef.current = null;
    const item = section.items.find(matches);
    setActiveTab(section.id);
    setHighlightTitle(item.title);
    setTimeout(() => {
      document
        .querySelector(`[data-material-card="${CSS.escape(item.title)}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 150);
    setTimeout(() => setHighlightTitle(null), 3500);
  }, [loading, sections]);

  useEffect(() => {
    if (!currentUser) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'internal_users', currentUser.uid));
        if (snap.exists()) {
          const d = snap.data();
          setUserName(
            d.name ||
              [d.firstName, d.lastName].filter(Boolean).join(' ') ||
              d.email ||
              currentUser.email ||
              'Unknown'
          );
        } else {
          setUserName(currentUser.displayName || currentUser.email || 'Unknown');
        }
      } catch (err) {
        console.error('Error resolving user name:', err);
      }
    })();
  }, [currentUser]);

  const openFile = (url, fileName) => {
    if (!url) return;
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    if (fileName) link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // View opens the file in a fullscreen in-page overlay instead of a new tab.
  const viewFile = (url, title) => {
    if (!url) return;
    setViewerDoc({ url, title });
  };

  // ---- Upload ----
  const openUpload = (entry) => {
    setUploadEntry(entry);
    setUploadFile(null);
    setUploadVersion(entry.doc?.fileUrl ? suggestNextVersion(entry.doc.version) : '1.0');
    setUploadNotes('');
    setUploadError('');
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!ALLOWED_EXT.test(file.name)) {
      setUploadError('Invalid file type. Use PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, or TXT.');
      e.target.value = '';
      return;
    }
    setUploadError('');
    setUploadFile(file);
  };

  const submitUpload = async () => {
    if (!uploadFile || !uploadVersion.trim()) {
      setUploadError('Select a file and enter a version number.');
      return;
    }
    setUploading(true);
    setUploadError('');
    try {
      await uploadNewVersion({
        docId: uploadEntry.doc?.id,
        title: uploadEntry.doc?.title || uploadEntry.title,
        catalogKey: uploadEntry.title,
        pillar,
        file: uploadFile,
        version: uploadVersion,
        notes: uploadNotes,
        user: { uid: currentUser?.uid, name: userName },
      });
      setUploadEntry(null);
      await loadTemplates();
    } catch (err) {
      console.error('Upload failed:', err);
      setUploadError(err.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // ---- Edit ----
  const sectionIdOfItem = (title) =>
    sections.find((s) => (s.items || []).some((i) => i.title === title))?.id || '';

  const openEdit = (entry) => {
    setEditEntry(entry);
    setEditTitle(entry.doc?.title || entry.name || entry.title);
    setEditDescription(entry.doc?.description || entry.description || '');
    setEditSectionId(sectionIdOfItem(entry.title));
  };

  // Renames only touch display fields (doc.title + structure item name). The
  // structure item's `title` is the stable key linking card ↔ doc ↔ portal, so
  // it never changes; catalogKey is backfilled to keep the doc linked. Picking
  // a different section moves the item there (appended at the end).
  const submitEdit = async () => {
    if (!editEntry) return;
    const name = editTitle.trim();
    if (!name) return;
    setSavingEdit(true);
    try {
      if (editEntry.doc?.id) {
        await updateTemplateDetails(editEntry.doc.id, {
          title: name,
          description: editDescription.trim(),
          catalogKey: editEntry.title,
        });
      }

      const currentSectionId = sectionIdOfItem(editEntry.title);
      const original =
        sections.flatMap((s) => s.items || []).find((i) => i.title === editEntry.title) ||
        { title: editEntry.title };
      const updatedItem = { ...original, name, description: editDescription.trim() };
      const moving = editSectionId && editSectionId !== currentSectionId;

      const nextSections = sections.map((s) => {
        if (moving && s.id === currentSectionId) {
          return { ...s, items: s.items.filter((i) => i.title !== editEntry.title) };
        }
        if (moving && s.id === editSectionId) {
          return { ...s, items: [...s.items, updatedItem] };
        }
        if (!moving) {
          return {
            ...s,
            items: s.items.map((i) => (i.title === editEntry.title ? updatedItem : i)),
          };
        }
        return s;
      });

      await persistStructure(nextSections);
      if (moving) setActiveTab(editSectionId);
      setEditEntry(null);
    } catch (err) {
      console.error('Edit failed:', err);
    } finally {
      setSavingEdit(false);
    }
  };

  // ---- History ----
  const openHistory = async (entry) => {
    setHistoryEntry(entry);
    setVersions([]);
    if (!entry.doc?.id) return;
    setHistoryLoading(true);
    try {
      setVersions(await fetchVersionHistory(entry.doc.id));
    } catch (err) {
      console.error('History load failed:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleRollback = async (versionId) => {
    if (!historyEntry?.doc?.id) return;
    setHistoryLoading(true);
    try {
      await rollbackToVersion(historyEntry.doc.id, versionId, {
        uid: currentUser?.uid,
        name: userName,
      });
      setVersions(await fetchVersionHistory(historyEntry.doc.id));
      await loadTemplates();
    } catch (err) {
      console.error('Rollback failed:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  // ---- Tailor HTML ----
  const openHtmlEditor = (entry) => {
    setHtmlEntry(entry);
    setHtmlDraft(entry.doc?.tailorHtml || '');
    setHtmlError('');
    setHtmlShowPreview(false);
  };

  const insertToken = (token) => {
    const snippet = `{{${token}}}`;
    const el = htmlTextareaRef.current;
    if (!el) {
      setHtmlDraft((prev) => prev + snippet);
      return;
    }
    const start = el.selectionStart ?? htmlDraft.length;
    const end = el.selectionEnd ?? htmlDraft.length;
    const next = htmlDraft.slice(0, start) + snippet + htmlDraft.slice(end);
    setHtmlDraft(next);
    requestAnimationFrame(() => {
      el.focus();
      el.selectionStart = el.selectionEnd = start + snippet.length;
    });
  };

  const submitHtml = async () => {
    const trimmed = htmlDraft.trim();
    if (!trimmed && !htmlEntry?.doc?.tailorHtml) {
      setHtmlError('Paste the template HTML before saving.');
      return;
    }
    if (!trimmed && !window.confirm('Save with no HTML? This disables tailoring for this template.')) {
      return;
    }
    setHtmlSaving(true);
    setHtmlError('');
    try {
      await saveTailorHtml({
        docId: htmlEntry.doc?.id,
        title: htmlEntry.doc?.title || htmlEntry.name || htmlEntry.title,
        catalogKey: htmlEntry.title,
        pillar,
        html: trimmed,
        tokens: extractTokens(trimmed),
        user: { uid: currentUser?.uid, name: userName },
      });
      setHtmlEntry(null);
      await loadTemplates();
    } catch (err) {
      console.error('Tailor HTML save failed:', err);
      setHtmlError(err.message || 'Failed to save. Please try again.');
    } finally {
      setHtmlSaving(false);
    }
  };

  const draftTokens = extractTokens(htmlDraft);
  const unknownTokens = draftTokens.filter((t) => !KNOWN_TOKEN_KEYS.has(t));

  // ---- Canva link ----
  const openCanvaEditor = (entry) => {
    setCanvaEntry(entry);
    setCanvaDraft(entry.doc?.canvaUrl || '');
    setCanvaError('');
  };

  const submitCanva = async () => {
    const trimmed = canvaDraft.trim();
    if (trimmed && !/^https?:\/\//i.test(trimmed)) {
      setCanvaError('Enter a full URL starting with https://');
      return;
    }
    if (
      !trimmed &&
      (canvaEntry?.doc?.canvaUrl || '').trim() &&
      !window.confirm('Remove the Canva link for this template?')
    ) {
      return;
    }
    setCanvaSaving(true);
    setCanvaError('');
    try {
      await saveCanvaUrl({
        docId: canvaEntry.doc?.id,
        title: canvaEntry.doc?.title || canvaEntry.name || canvaEntry.title,
        catalogKey: canvaEntry.title,
        pillar,
        url: trimmed,
        user: { uid: currentUser?.uid, name: userName },
      });
      setCanvaEntry(null);
      await loadTemplates();
    } catch (err) {
      console.error('Canva link save failed:', err);
      setCanvaError(err.message || 'Failed to save. Please try again.');
    } finally {
      setCanvaSaving(false);
    }
  };

  // ---- Download format toggles (saved immediately per click) ----
  const enabledFormatsOf = (docData) =>
    Array.isArray(docData?.tailorFormats) && docData.tailorFormats.length > 0
      ? normalizeFormatKeys(docData.tailorFormats)
      : DEFAULT_TAILOR_FORMATS;

  const toggleFormat = async (entry, fmtKey) => {
    const current = enabledFormatsOf(entry.doc);
    const isOn = current.includes(fmtKey);
    if (isOn && current.length === 1) return; // always keep at least one format
    const next = isOn ? current.filter((f) => f !== fmtKey) : [...current, fmtKey];

    // Optimistic UI; reconcile with a reload only on failure or first-time doc creation.
    if (entry.doc) {
      setSections((prev) =>
        prev.map((s) => ({
          ...s,
          entries: (s.entries || []).map((e) =>
            e.title === entry.title && e.doc ? { ...e, doc: { ...e.doc, tailorFormats: next } } : e
          ),
        }))
      );
    }
    try {
      await saveTailorFormats({
        docId: entry.doc?.id,
        title: entry.doc?.title || entry.name || entry.title,
        catalogKey: entry.title,
        pillar,
        formats: next,
        user: { uid: currentUser?.uid, name: userName },
      });
      if (!entry.doc?.id) await loadTemplates();
    } catch (err) {
      console.error('Saving download formats failed:', err);
      await loadTemplates();
    }
  };

  // ---- Structure (sections & items) ----
  const persistStructure = async (nextSections) => {
    await saveTemplateStructure(structureId, nextSections);
    await loadTemplates();
  };

  // Move a template up/down within its section. Item order in the structure
  // doc IS the display order on both this page and the portal. Updates the UI
  // optimistically (items + entries together) and saves on every click.
  const moveItem = async (entry, direction) => {
    const swapByTitle = (arr) => {
      const idx = arr.findIndex((i) => i.title === entry.title);
      const target = idx + direction;
      if (idx === -1 || target < 0 || target >= arr.length) return arr;
      const next = [...arr];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    };
    const nextSections = sections.map((s) =>
      (s.items || []).some((i) => i.title === entry.title)
        ? { ...s, items: swapByTitle(s.items), entries: s.entries ? swapByTitle(s.entries) : s.entries }
        : s
    );
    setSections(nextSections);
    try {
      await saveTemplateStructure(structureId, nextSections);
    } catch (err) {
      console.error('Reorder failed:', err);
      await loadTemplates();
    }
  };

  // Save a template's emoji icon on its structure item (optimistic, no reload).
  // Pass '' to clear it and fall back to the file-type icon.
  const saveEmoji = async (entry, emoji) => {
    const patch = (arr) =>
      (arr || []).map((i) => (i.title === entry.title ? { ...i, emoji } : i));
    const nextSections = sections.map((s) => ({
      ...s,
      items: patch(s.items),
      entries: s.entries ? patch(s.entries) : s.entries,
    }));
    setSections(nextSections);
    setEmojiTarget(null);
    try {
      await saveTemplateStructure(structureId, nextSections);
    } catch (err) {
      console.error('Icon save failed:', err);
      await loadTemplates();
    }
  };

  // Section and new-template emojis only patch the modal draft; the matching
  // submit handler persists them. Existing templates save the emoji immediately.
  const pickEmoji = (emoji) => {
    if (emojiTarget?.kind === 'section') {
      setSectionModal((m) => (m ? { ...m, emoji } : m));
      setEmojiTarget(null);
    } else if (emojiTarget?.kind === 'newItem') {
      setItemModal((m) => (m ? { ...m, emoji } : m));
      setEmojiTarget(null);
    } else if (emojiTarget?.kind === 'item') {
      saveEmoji(emojiTarget.entry, emoji);
    }
  };

  // Move a section left/right in the tab row (and up/down on the portal —
  // section order in the structure doc IS the display order everywhere).
  // Optimistic swap, saved on every click; reload only on failure.
  const moveSection = async (sectionId, direction) => {
    const idx = sections.findIndex((s) => s.id === sectionId);
    const target = idx + direction;
    if (idx === -1 || target < 0 || target >= sections.length) return;
    const next = [...sections];
    [next[idx], next[target]] = [next[target], next[idx]];
    setSections(next);
    try {
      await saveTemplateStructure(structureId, next);
    } catch (err) {
      console.error('Section reorder failed:', err);
      await loadTemplates();
    }
  };

  const submitSection = async () => {
    const label = (sectionModal?.label || '').trim();
    if (!label) {
      setSectionError('Enter a section name.');
      return;
    }
    const clash = sections.some(
      (s) => s.id !== sectionModal.sectionId && s.label.toLowerCase() === label.toLowerCase()
    );
    if (clash) {
      setSectionError('A section with this name already exists.');
      return;
    }
    setSavingSection(true);
    setSectionError('');
    try {
      if (sectionModal.mode === 'rename') {
        await persistStructure(
          sections.map((s) =>
            s.id === sectionModal.sectionId ? { ...s, label, emoji: sectionModal.emoji || '' } : s
          )
        );
      } else {
        const newSection = {
          id: makeSectionId(label, sections),
          label,
          emoji: sectionModal.emoji || '',
          items: [],
        };
        await persistStructure([...sections, newSection]);
        setActiveTab(newSection.id);
      }
      setSectionModal(null);
    } catch (err) {
      console.error('Section save failed:', err);
      setSectionError('Failed to save. Please try again.');
    } finally {
      setSavingSection(false);
    }
  };

  const deleteSection = async () => {
    const section = sections.find((s) => s.id === sectionModal?.sectionId);
    if (!section) return;
    if (section.items.length > 0) return; // guarded in UI too
    if (!window.confirm(`Delete the "${section.label}" section?`)) return;
    setSavingSection(true);
    try {
      await persistStructure(sections.filter((s) => s.id !== section.id));
      setSectionModal(null);
    } catch (err) {
      console.error('Section delete failed:', err);
      setSectionError('Failed to delete. Please try again.');
    } finally {
      setSavingSection(false);
    }
  };

  const submitItem = async () => {
    const name = (itemModal?.name || '').trim();
    if (!name) {
      setItemError('Enter a template name.');
      return;
    }
    const allTitles = sections.flatMap((s) => s.items.map((i) => i.title.toLowerCase()));
    if (allTitles.includes(name.toLowerCase())) {
      setItemError('A template with this name already exists.');
      return;
    }
    setSavingItem(true);
    setItemError('');
    try {
      await persistStructure(
        sections.map((s) =>
          s.id === activeTab
            ? {
                ...s,
                items: [
                  ...s.items,
                  {
                    title: name,
                    name,
                    description: (itemModal.description || '').trim(),
                    ...(itemModal.emoji ? { emoji: itemModal.emoji } : {}),
                  },
                ],
              }
            : s
        )
      );
      setItemModal(null);
    } catch (err) {
      console.error('Add template failed:', err);
      setItemError('Failed to save. Please try again.');
    } finally {
      setSavingItem(false);
    }
  };

  const removeItem = async (entry) => {
    const label = entry.name || entry.title;
    const message = entry.doc?.fileUrl
      ? `Remove "${label}" from this section? It disappears from the portal, but its file, version history, and tailoring HTML are kept — adding a template with the same name later restores them.`
      : `Remove "${label}" from this section?`;
    if (!window.confirm(message)) return;
    try {
      await persistStructure(
        sections.map((s) =>
          s.id === activeTab ? { ...s, items: s.items.filter((i) => i.title !== entry.title) } : s
        )
      );
    } catch (err) {
      console.error('Remove template failed:', err);
    }
  };

  const card = 'bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800';
  const subtle = darkMode ? 'text-gray-400' : 'text-gray-500';
  const activeSection = sections.find((s) => s.id === activeTab) || null;
  const entries = activeSection?.entries || [];

  return (
    <div className="min-h-screen p-4 md:p-6 relative">
      <PortalBackground />
      <div className="relative max-w-6xl mx-auto">
        <div className="relative flex items-center justify-between mb-6 min-h-[40px]">
          <BackButton to={backTo} />
          <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white text-center absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap">
            {title}
          </h1>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          {sections.map((section) => (
            <div
              key={section.id}
              className={`inline-flex items-center rounded-full text-sm font-medium transition-colors ${
                activeTab === section.id
                  ? `${accent.tabActive} text-white`
                  : darkMode
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              <button
                onClick={() => setActiveTab(section.id)}
                className={`px-4 py-2 ${activeTab === section.id ? 'pr-1' : ''}`}
              >
                {section.emoji && <span className="mr-1.5">{section.emoji}</span>}
                {section.label}
              </button>
              {activeTab === section.id && (
                <button
                  onClick={() => {
                    setSectionError('');
                    setSectionModal({
                      mode: 'rename',
                      sectionId: section.id,
                      label: section.label,
                      emoji: section.emoji || '',
                    });
                  }}
                  className="pl-1 pr-3 py-2 text-white/80 hover:text-white"
                  title="Rename section"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
          <button
            onClick={() => {
              setSectionError('');
              setSectionModal({ mode: 'add', label: '', emoji: '' });
            }}
            className={`inline-flex items-center gap-1 px-3 py-2 rounded-full text-sm font-medium border border-dashed transition-colors ${
              darkMode
                ? 'border-gray-600 text-gray-400 hover:text-gray-200 hover:border-gray-500'
                : 'border-gray-300 text-gray-500 hover:text-gray-700 hover:border-gray-400'
            }`}
            title="Add a new section"
          >
            <Plus className="w-4 h-4" /> Add section
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <BBALoader size={40} />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {entries.map((entry, entryIndex) => {
              const docData = entry.doc;
              const hasFile = !!docData?.fileUrl;
              const hasHtml = !!(docData?.tailorHtml || '').trim();
              const canvaUrl = (docData?.canvaUrl || '').trim();
              const tokenCount = hasHtml
                ? (docData.tailorTokens || extractTokens(docData.tailorHtml)).length
                : 0;
              const type = docData?.type || getFileType(docData?.fileName || '');
              const description = docData?.description || entry.description;
              const menuOpen = menuKey === entry.title;
              const panel = `rounded-xl p-3.5 bg-gray-50 dark:bg-gray-800/60`;
              const chipGood =
                'inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300';
              const chipWarn =
                'inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300';
              const chipNeutral =
                'inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
              const iconBtn = `p-2 rounded-md border transition-colors ${
                darkMode
                  ? 'border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white'
                  : 'border-gray-200 text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              }`;
              // Labeled secondary action, sized to match the primary "New version" button.
              const secondaryBtn = `inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-md border transition-colors ${
                darkMode
                  ? 'border-gray-600 text-gray-200 hover:bg-gray-700 hover:text-white'
                  : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-100 hover:text-gray-900'
              }`;
              return (
                <div
                  key={entry.title}
                  data-material-card={entry.title}
                  className={`${card} rounded-2xl shadow-sm p-5 flex flex-col ${
                    highlightTitle === entry.title ? 'ring-2 ring-red-400 dark:ring-red-500' : ''
                  }`}
                >
                  {/* Header: icon + title/description + kebab menu */}
                  <div className="flex items-start gap-3 mb-4">
                    <button
                      type="button"
                      onClick={() => setEmojiTarget({ kind: 'item', entry })}
                      title="Choose an icon for this template"
                      className={`shrink-0 w-11 h-11 rounded-lg flex items-center justify-center transition-colors ${
                        darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
                      }`}
                    >
                      {entry.emoji ? (
                        <span className="text-2xl leading-none">{entry.emoji}</span>
                      ) : (
                        <TypeIcon type={hasFile ? type : 'DOC'} className="h-6 w-6" />
                      )}
                    </button>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white leading-tight">
                        {docData?.title || entry.name}
                      </h3>
                      {description && (
                        <p className={`text-sm ${subtle} mt-0.5 line-clamp-2`}>{description}</p>
                      )}
                    </div>
                    <div className="relative shrink-0 flex items-center">
                      <button
                        onClick={() => moveItem(entry, -1)}
                        disabled={entryIndex === 0}
                        className={`p-1.5 rounded-md disabled:opacity-25 disabled:cursor-default ${
                          darkMode
                            ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700 disabled:hover:bg-transparent disabled:hover:text-gray-400'
                            : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:hover:bg-transparent disabled:hover:text-gray-400'
                        }`}
                        title="Move up"
                      >
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => moveItem(entry, 1)}
                        disabled={entryIndex === entries.length - 1}
                        className={`p-1.5 rounded-md disabled:opacity-25 disabled:cursor-default ${
                          darkMode
                            ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700 disabled:hover:bg-transparent disabled:hover:text-gray-400'
                            : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:hover:bg-transparent disabled:hover:text-gray-400'
                        }`}
                        title="Move down"
                      >
                        <ChevronDown className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setMenuKey(menuOpen ? null : entry.title)}
                        className={`p-1.5 rounded-md ${
                          darkMode
                            ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
                            : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'
                        }`}
                        title="More options"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                      {menuOpen && (
                        <>
                          <div className="fixed inset-0 z-10" onClick={() => setMenuKey(null)} />
                          <div
                            className={`absolute right-0 top-8 z-20 w-44 rounded-lg shadow-lg border py-1 ${
                              darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                            }`}
                          >
                            <button
                              onClick={() => {
                                setMenuKey(null);
                                openEdit(entry);
                              }}
                              className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left ${
                                darkMode ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              <Pencil className="w-3.5 h-3.5" /> Edit details
                            </button>
                            <button
                              onClick={() => {
                                setMenuKey(null);
                                removeItem(entry);
                              }}
                              className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left ${
                                darkMode ? 'text-red-400 hover:bg-red-900/30' : 'text-red-600 hover:bg-red-50'
                              }`}
                            >
                              <Trash2 className="w-3.5 h-3.5" /> Remove from section
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Client file — what clients see for View / Download */}
                  <div className={panel}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className={`text-[10px] font-semibold uppercase tracking-wider ${subtle}`}>
                        Client file
                      </span>
                      {hasFile ? (
                        <span className={chipGood}>
                          <CheckCircle className="w-3 h-3" /> v{docData.version}
                        </span>
                      ) : (
                        <span className={chipWarn}>
                          <AlertTriangle className="w-3 h-3" /> No file yet
                        </span>
                      )}
                    </div>
                    <p className={`text-xs ${subtle} mb-3`}>
                      {hasFile
                        ? `${type} · Updated ${formatDate(docData.updatedAt)}`
                        : 'Upload the document clients will view and download.'}
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        onClick={() => openUpload(entry)}
                        className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-md text-white ${accent.button}`}
                      >
                        <Upload className="w-3.5 h-3.5" /> {hasFile ? 'New version' : 'Upload file'}
                      </button>
                      {hasFile && (
                        <>
                          <button
                            onClick={() => viewFile(getTemplateViewUrl(docData), docData?.title || entry.name)}
                            className={secondaryBtn}
                            title="View file"
                          >
                            <Eye className="w-3.5 h-3.5" /> View file
                          </button>
                          <button
                            onClick={() => openFile(docData.fileUrl, docData.fileName)}
                            className={secondaryBtn}
                            title="Download file"
                          >
                            <Download className="w-3.5 h-3.5" /> Download
                          </button>
                          <button
                            onClick={() => openHistory(entry)}
                            className={secondaryBtn}
                            title="Version history"
                          >
                            <History className="w-3.5 h-3.5" /> Version history
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Canva link — open the source design in Canva */}
                  {showCanvaLink && (
                    <div className={`${panel} mt-3`}>
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className={`text-[10px] font-semibold uppercase tracking-wider ${subtle}`}>
                          Canva link
                        </span>
                        {canvaUrl ? (
                          <span className={chipGood}>
                            <CheckCircle className="w-3 h-3" /> Linked
                          </span>
                        ) : (
                          <span className={chipNeutral}>Not set</span>
                        )}
                      </div>
                      <p className={`text-xs ${subtle} mb-3 truncate`}>
                        {canvaUrl || 'Paste the Canva share link for this template.'}
                      </p>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openCanvaEditor(entry)}
                          className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-md bg-cyan-600 text-white hover:bg-cyan-700"
                        >
                          <Link2 className="w-3.5 h-3.5" /> {canvaUrl ? 'Edit link' : 'Add link'}
                        </button>
                        {canvaUrl && (
                          <button
                            onClick={() => window.open(canvaUrl, '_blank', 'noopener,noreferrer')}
                            className={iconBtn}
                            title="Open in Canva"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Tailoring — token HTML that powers the portal Tailor button */}
                  <div className={`${panel} mt-3`}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className={`text-[10px] font-semibold uppercase tracking-wider ${subtle}`}>
                        Tailoring HTML
                      </span>
                      {hasHtml ? (
                        <span className={chipGood}>
                          <Sparkles className="w-3 h-3" /> Ready · {tokenCount} token{tokenCount === 1 ? '' : 's'}
                        </span>
                      ) : (
                        <span className={chipWarn}>
                          <AlertTriangle className="w-3 h-3" /> Not set up
                        </span>
                      )}
                    </div>
                    <p className={`text-xs ${subtle} mb-3`}>
                      {hasHtml
                        ? `Updated ${formatDate(docData.tailorHtmlUpdatedAt)}${
                            docData.tailorHtmlUpdatedByName ? ` by ${docData.tailorHtmlUpdatedByName}` : ''
                          }`
                        : 'Paste HTML with {{tokens}} to enable the portal Tailor button.'}
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        onClick={() => openHtmlEditor(entry)}
                        className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-md bg-purple-600 text-white hover:bg-purple-700"
                      >
                        <Code2 className="w-3.5 h-3.5" /> {hasHtml ? 'Edit HTML' : 'Add HTML'}
                      </button>
                      {hasHtml && (
                        <button
                          onClick={() => setPreviewEntry(entry)}
                          className={secondaryBtn}
                          title="Preview with sample data"
                        >
                          <Eye className="w-3.5 h-3.5" /> Preview with sample data
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setPromptCopied(false);
                          setHowToEntry(entry);
                        }}
                        className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-md bg-red-600 text-white hover:bg-red-700"
                        title="How to update this template's HTML"
                      >
                        <HelpCircle className="w-3.5 h-3.5" /> How to update
                      </button>
                    </div>

                    {/* Download options — which formats clients can export after tailoring */}
                    <div className="mt-3">
                      <p className={`text-[10px] font-semibold uppercase tracking-wider mb-1.5 ${subtle}`}>
                        Download options
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {TAILOR_FORMATS.map((f) => {
                          const on = enabledFormatsOf(docData).includes(f.key);
                          return (
                            <button
                              key={f.key}
                              onClick={() => toggleFormat(entry, f.key)}
                              title={on ? `Disable ${f.label} download` : `Enable ${f.label} download`}
                              className={`text-[11px] font-medium px-2.5 py-1 rounded-full border transition-colors ${
                                on
                                  ? 'bg-purple-600 border-purple-600 text-white hover:bg-purple-700'
                                  : darkMode
                                  ? 'border-gray-600 text-gray-400 hover:text-gray-200 hover:border-gray-500'
                                  : 'border-gray-300 text-gray-500 hover:text-gray-800 hover:border-gray-400'
                              }`}
                            >
                              {f.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Add template tile */}
            <button
              onClick={() => {
                setItemError('');
                setItemModal({ name: '', description: '' });
              }}
              className={`rounded-lg border-2 border-dashed p-5 flex flex-col items-center justify-center gap-2 min-h-[180px] transition-colors ${
                darkMode
                  ? 'border-gray-600 text-gray-400 hover:text-gray-200 hover:border-gray-500'
                  : 'border-gray-300 text-gray-500 hover:text-gray-700 hover:border-gray-400'
              }`}
            >
              <Plus className="w-6 h-6" />
              <span className="text-sm font-medium">Add template</span>
            </button>
          </div>
        )}
      </div>

      {/* Upload modal */}
      <Modal isOpen={!!uploadEntry} onClose={() => !uploading && setUploadEntry(null)} maxWidth="md">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">
          {uploadEntry?.doc?.fileUrl ? 'Upload new version' : 'Upload file'}
        </h2>
        <p className={`text-sm ${subtle} mb-4`}>{uploadEntry?.doc?.title || uploadEntry?.name}</p>

        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Version number <span className={`font-normal ${subtle}`}>(auto-suggested)</span>
        </label>
        <input
          type="text"
          value={uploadVersion}
          onChange={(e) => setUploadVersion(e.target.value)}
          placeholder="e.g. 1.0, 2.1"
          className={`w-full px-3 py-2 mb-4 border rounded-md ${
            darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
          }`}
        />

        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Change notes (optional)
        </label>
        <textarea
          value={uploadNotes}
          onChange={(e) => setUploadNotes(e.target.value)}
          rows={2}
          placeholder="What changed in this version?"
          className={`w-full px-3 py-2 mb-4 border rounded-md ${
            darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
          }`}
        />

        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">File</label>
        <input
          type="file"
          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
          onChange={handleFileChange}
          className={`w-full text-sm mb-2 ${subtle}`}
        />
        {uploadFile && <p className={`text-xs ${subtle} mb-2`}>Selected: {uploadFile.name}</p>}
        <p className={`text-xs ${subtle} mb-4`}>PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, or TXT.</p>

        {uploadError && (
          <p className="text-sm text-red-600 mb-3 flex items-center gap-1">
            <AlertTriangle className="w-4 h-4" /> {uploadError}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <button
            onClick={() => setUploadEntry(null)}
            disabled={uploading}
            className={`px-4 py-2 rounded-md text-sm ${
              darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'
            }`}
          >
            Cancel
          </button>
          <button
            onClick={submitUpload}
            disabled={uploading || !uploadFile || !uploadVersion.trim()}
            className={`px-4 py-2 rounded-md text-sm font-medium text-white ${accent.button} disabled:bg-gray-400 disabled:cursor-not-allowed`}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </button>
        </div>
      </Modal>

      {/* Edit modal */}
      <Modal isOpen={!!editEntry} onClose={() => !savingEdit && setEditEntry(null)} maxWidth="md">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Edit details</h2>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title</label>
        <input
          type="text"
          value={editTitle}
          onChange={(e) => setEditTitle(e.target.value)}
          className={`w-full px-3 py-2 mb-4 border rounded-md ${
            darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
          }`}
        />
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Section
        </label>
        <select
          value={editSectionId}
          onChange={(e) => setEditSectionId(e.target.value)}
          className={`w-full px-3 py-2 mb-4 border rounded-md ${
            darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300 bg-white'
          }`}
        >
          {sections.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </select>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Description
        </label>
        <textarea
          value={editDescription}
          onChange={(e) => setEditDescription(e.target.value)}
          rows={4}
          className={`w-full px-3 py-2 mb-4 border rounded-md ${
            darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
          }`}
        />
        <div className="flex justify-end gap-2">
          <button
            onClick={() => setEditEntry(null)}
            disabled={savingEdit}
            className={`px-4 py-2 rounded-md text-sm ${
              darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'
            }`}
          >
            Cancel
          </button>
          <button
            onClick={submitEdit}
            disabled={savingEdit}
            className={`px-4 py-2 rounded-md text-sm font-medium text-white ${accent.button} disabled:bg-gray-400`}
          >
            {savingEdit ? 'Saving...' : 'Save'}
          </button>
        </div>
      </Modal>

      {/* History modal */}
      <Modal isOpen={!!historyEntry} onClose={() => setHistoryEntry(null)} maxWidth="2xl">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">Version history</h2>
        <p className={`text-sm ${subtle} mb-4`}>{historyEntry?.doc?.title || historyEntry?.name}</p>

        {historyLoading ? (
          <p className={subtle}>Loading...</p>
        ) : versions.length === 0 ? (
          <p className={`${subtle} py-6 text-center`}>No version history yet.</p>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {versions.map((v, idx) => (
              <div
                key={v.id}
                className={`flex items-center justify-between p-3 rounded-md ${
                  darkMode ? 'bg-gray-700/60' : 'bg-gray-50'
                }`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">
                      v{v.version}
                    </span>
                    {idx === 0 && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">
                        Current
                      </span>
                    )}
                  </div>
                  <p className={`text-xs ${subtle} truncate`}>
                    {v.uploadedByName} · {formatDate(v.uploadedAt)}
                    {v.notes ? ` · ${v.notes}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2 ml-3 shrink-0">
                  <button
                    onClick={() =>
                      viewFile(
                        getTemplateViewUrl(v),
                        `${historyEntry?.doc?.title || historyEntry?.name} — v${v.version}`
                      )
                    }
                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
                    title="View this version"
                  >
                    <Eye className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => openFile(v.fileUrl, v.fileName)}
                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-green-600 text-white hover:bg-green-700"
                    title="Download this version"
                  >
                    <Download className="w-3.5 h-3.5" />
                  </button>
                  {idx !== 0 && (
                    <button
                      onClick={() => handleRollback(v.id)}
                      className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded text-white ${accent.button}`}
                      title="Roll back to this version"
                    >
                      <RotateCcw className="w-3.5 h-3.5" /> Restore
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end mt-4">
          <button
            onClick={() => setHistoryEntry(null)}
            className={`px-4 py-2 rounded-md text-sm ${
              darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'
            }`}
          >
            Close
          </button>
        </div>
      </Modal>

      {/* Tailor HTML modal */}
      {/* "How to update" walkthrough — file → Claude → HTML → paste into editor */}
      <Modal isOpen={!!howToEntry} onClose={() => setHowToEntry(null)} maxWidth="3xl">
        {(() => {
          const templateName = howToEntry?.doc?.title || howToEntry?.name || howToEntry?.title || 'this template';
          const promptText = buildClaudePrompt(templateName);
          const stepNum = 'w-7 h-7 rounded-full bg-red-600 text-white text-sm font-bold flex items-center justify-center shrink-0';
          const stepBody = 'flex-1 min-w-0';
          const stepTitle = 'text-sm font-semibold text-gray-900 dark:text-white';
          const stepText = `text-sm ${subtle} mt-0.5`;
          return (
            <div className="max-h-[80vh] overflow-y-auto pr-1">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-1 text-center">
                How to update this template
              </h2>
              <p className={`text-sm ${subtle} mb-5 text-center`}>
                {templateName} — turn the latest version of the file into tailoring HTML in four steps.
              </p>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className={stepNum}>1</div>
                  <div className={stepBody}>
                    <p className={stepTitle}>Upload the latest file to Claude</p>
                    <p className={stepText}>
                      Open Claude and attach the newest version of the document (Word, PDF, or Google Doc export).
                      Make sure it is the final copy — whatever you upload is what clients will get.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className={stepNum}>2</div>
                  <div className={stepBody}>
                    <p className={stepTitle}>Paste this prompt into Claude with the file</p>
                    <p className={stepText}>
                      It tells Claude exactly how to build the HTML and lists every token our portal can fill in.
                      Copy it as-is.
                    </p>
                    <div className="relative mt-2">
                      <textarea
                        readOnly
                        value={promptText}
                        rows={12}
                        onFocus={(e) => e.target.select()}
                        className={`w-full text-xs font-mono rounded-md border p-3 pr-28 resize-y ${
                          darkMode
                            ? 'bg-gray-900 border-gray-700 text-gray-200'
                            : 'bg-gray-50 border-gray-200 text-gray-800'
                        }`}
                      />
                      <button
                        type="button"
                        onClick={copyClaudePrompt}
                        className={`absolute top-2 right-2 inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md text-white ${
                          promptCopied ? 'bg-green-600' : 'bg-red-600 hover:bg-red-700'
                        }`}
                      >
                        {promptCopied ? (
                          <>
                            <Check className="w-3.5 h-3.5" /> Copied
                          </>
                        ) : (
                          <>
                            <Copy className="w-3.5 h-3.5" /> Copy prompt
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className={stepNum}>3</div>
                  <div className={stepBody}>
                    <p className={stepTitle}>Copy the HTML Claude gives back</p>
                    <p className={stepText}>
                      Claude will reply with one code block of HTML. Copy the whole block. If it added any
                      explanation outside the code block, ignore that part.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className={stepNum}>4</div>
                  <div className={stepBody}>
                    <p className={stepTitle}>Replace the HTML on this card</p>
                    <p className={stepText}>
                      Close this window and click <strong>Edit HTML</strong> on the card. Select everything in the
                      editor, delete it, paste the new HTML, then click <strong>Preview with sample data</strong> to
                      check it looks right before you save.
                    </p>
                  </div>
                </div>
              </div>

              <div className={`mt-5 rounded-xl p-3 text-xs bg-gray-50 dark:bg-gray-800/60 ${subtle}`}>
                <span className="font-semibold text-gray-700 dark:text-gray-200">Tip:</span> If the preview shows a
                token as blank or spelled out literally, the token name is misspelled. Compare it against the token
                list inside the editor and fix the spelling.
              </div>

              <div className="flex justify-end gap-2 mt-5">
                <button
                  onClick={() => {
                    const entry = howToEntry;
                    setHowToEntry(null);
                    if (entry) openHtmlEditor(entry);
                  }}
                  className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-md bg-purple-600 text-white hover:bg-purple-700"
                >
                  <Code2 className="w-3.5 h-3.5" /> Open Edit HTML
                </button>
                <button
                  onClick={() => setHowToEntry(null)}
                  className={`text-sm font-medium px-4 py-2 rounded-md ${
                    darkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Close
                </button>
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* Sample-data preview of a card's saved tailoring HTML */}
      <Modal isOpen={!!previewEntry} onClose={() => setPreviewEntry(null)} maxWidth="3xl">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-1 text-center">
          Preview with sample data
        </h2>
        <p className={`text-sm ${subtle} mb-4 text-center`}>
          {previewEntry?.doc?.title || previewEntry?.name} — tokens filled with sample company info,
          as a client would see it after tailoring.
        </p>
        {previewEntry && (
          <iframe
            title="Template preview"
            sandbox=""
            srcDoc={wrapTailoredHtml(applyTokens(previewEntry.doc?.tailorHtml || '', buildSampleValues()))}
            className="w-full h-[70vh] rounded-md border border-gray-200 dark:border-gray-700 bg-white mb-3"
          />
        )}
        <div className="flex justify-end gap-2">
          <button
            onClick={() => {
              const entry = previewEntry;
              setPreviewEntry(null);
              if (entry) openHtmlEditor(entry);
            }}
            className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-md bg-purple-600 text-white hover:bg-purple-700"
          >
            <Code2 className="w-3.5 h-3.5" /> Edit HTML
          </button>
          <button
            onClick={() => setPreviewEntry(null)}
            className={`text-sm font-medium px-4 py-2 rounded-md ${
              darkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Close
          </button>
        </div>
      </Modal>

      <Modal isOpen={!!htmlEntry} onClose={() => !htmlSaving && setHtmlEntry(null)} maxWidth="3xl">
        <div className="max-h-[80vh] overflow-y-auto pr-1">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">
          Tailoring HTML
        </h2>
        <p className={`text-sm ${subtle} mb-4`}>
          {htmlEntry?.doc?.title || htmlEntry?.name} — paste the template as HTML with{' '}
          <code className="px-1 rounded bg-gray-100 dark:bg-gray-700 text-purple-600 dark:text-purple-300">
            {'{{tokens}}'}
          </code>{' '}
          where client data goes. When a client hits Tailor on the portal, tokens are swapped for
          their company info and the result downloads as a PDF.
        </p>

        <div className="mb-3">
          <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Available tokens — paste HTML that already contains them (detected automatically) or
            click one to insert at the cursor
          </p>
          <div className="flex flex-wrap gap-1.5">
            {TAILOR_TOKENS.map((t) => (
              <button
                key={t.token}
                type="button"
                onClick={() => insertToken(t.token)}
                title={t.hint || t.label}
                className={`text-[11px] font-mono px-2 py-0.5 rounded-full border transition-colors ${
                  darkMode
                    ? 'border-gray-600 text-purple-300 hover:bg-gray-700'
                    : 'border-gray-300 text-purple-700 hover:bg-purple-50'
                }`}
              >
                {`{{${t.token}}}`}
              </button>
            ))}
          </div>
        </div>

        <textarea
          ref={htmlTextareaRef}
          value={htmlDraft}
          onChange={(e) => setHtmlDraft(e.target.value)}
          rows={12}
          spellCheck={false}
          placeholder={'<h1>{{companyName}} Offer Letter</h1>\n<img src="{{logoUrl}}" alt="Logo" style="max-height:80px">\n<p>Dear [Candidate Name], ...</p>'}
          className={`w-full px-3 py-2 border rounded-md font-mono text-xs leading-relaxed ${
            darkMode ? 'bg-gray-900 border-gray-600 text-gray-100' : 'bg-gray-50 border-gray-300 text-gray-800'
          }`}
        />

        <div className="flex items-center justify-between mt-2 mb-3 gap-3">
          <div className="flex flex-wrap items-center gap-1.5 min-w-0">
            {draftTokens.length > 0 ? (
              draftTokens.map((t) => (
                <span
                  key={t}
                  className={`text-[11px] font-mono px-2 py-0.5 rounded-full ${
                    KNOWN_TOKEN_KEYS.has(t)
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                      : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                  }`}
                >
                  {t}
                </span>
              ))
            ) : (
              <span className={`text-xs ${subtle}`}>No tokens detected yet.</span>
            )}
          </div>
          <button
            type="button"
            onClick={() => setHtmlShowPreview((v) => !v)}
            disabled={!htmlDraft.trim()}
            className={`shrink-0 inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded ${
              darkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            } disabled:opacity-50`}
          >
            <Eye className="w-3.5 h-3.5" /> {htmlShowPreview ? 'Hide preview' : 'Preview with sample data'}
          </button>
        </div>

        {unknownTokens.length > 0 && (
          <p className="text-xs text-amber-600 dark:text-amber-400 mb-3 flex items-start gap-1">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>
              Unrecognized token{unknownTokens.length === 1 ? '' : 's'}:{' '}
              {unknownTokens.map((t) => `{{${t}}}`).join(', ')} — these will be blank in client
              documents. Use the tokens listed above.
            </span>
          </p>
        )}

        {htmlShowPreview && htmlDraft.trim() && (
          <iframe
            title="Template preview"
            sandbox=""
            srcDoc={wrapTailoredHtml(applyTokens(htmlDraft, buildSampleValues()))}
            className="w-full h-80 rounded-md border border-gray-200 dark:border-gray-700 bg-white mb-3"
          />
        )}

        {htmlError && (
          <p className="text-sm text-red-600 mb-3 flex items-center gap-1">
            <AlertTriangle className="w-4 h-4" /> {htmlError}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <button
            onClick={() => setHtmlEntry(null)}
            disabled={htmlSaving}
            className={`px-4 py-2 rounded-md text-sm ${
              darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'
            }`}
          >
            Cancel
          </button>
          <button
            onClick={submitHtml}
            disabled={htmlSaving}
            className="px-4 py-2 rounded-md text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400"
          >
            {htmlSaving ? 'Saving...' : 'Save tailoring HTML'}
          </button>
        </div>
        </div>
      </Modal>

      {/* Section add/rename modal */}
      <Modal isOpen={!!sectionModal} onClose={() => !savingSection && setSectionModal(null)} maxWidth="md">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          {sectionModal?.mode === 'rename' ? 'Rename section' : 'Add section'}
        </h2>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Section name
        </label>
        <div className="flex items-center gap-3 mb-4">
          <button
            type="button"
            onClick={() => setEmojiTarget({ kind: 'section' })}
            title="Choose an icon for this section"
            className={`shrink-0 w-11 h-11 rounded-lg flex items-center justify-center transition-colors ${
              darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
            }`}
          >
            {sectionModal?.emoji ? (
              <span className="text-2xl leading-none">{sectionModal.emoji}</span>
            ) : (
              <SmilePlus className={`w-5 h-5 ${subtle}`} />
            )}
          </button>
          <input
            type="text"
            value={sectionModal?.label || ''}
            onChange={(e) => setSectionModal((m) => ({ ...m, label: e.target.value }))}
            placeholder={sectionPlaceholder}
            autoFocus
            className={`flex-1 px-3 py-2 border rounded-md ${
              darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
            }`}
          />
        </div>
        {sectionModal?.mode === 'rename' && (() => {
          const idx = sections.findIndex((s) => s.id === sectionModal.sectionId);
          const posBtn = `p-1.5 rounded-md border transition-colors disabled:opacity-25 disabled:cursor-default ${
            darkMode
              ? 'border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white disabled:hover:bg-transparent disabled:hover:text-gray-300'
              : 'border-gray-200 text-gray-600 hover:bg-gray-100 hover:text-gray-900 disabled:hover:bg-transparent disabled:hover:text-gray-600'
          }`;
          return (
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Position <span className={`font-normal ${subtle}`}>({idx + 1} of {sections.length})</span>
              </span>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => moveSection(sectionModal.sectionId, -1)}
                  disabled={savingSection || idx <= 0}
                  className={posBtn}
                  title="Move section up"
                >
                  <ChevronUp className="w-4 h-4" />
                </button>
                <button
                  onClick={() => moveSection(sectionModal.sectionId, 1)}
                  disabled={savingSection || idx === sections.length - 1}
                  className={posBtn}
                  title="Move section down"
                >
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })()}
        {sectionError && (
          <p className="text-sm text-red-600 mb-3 flex items-center gap-1">
            <AlertTriangle className="w-4 h-4" /> {sectionError}
          </p>
        )}
        <div className="flex items-center justify-between gap-2">
          <div>
            {sectionModal?.mode === 'rename' &&
              (sections.find((s) => s.id === sectionModal.sectionId)?.items.length === 0 ? (
                <button
                  onClick={deleteSection}
                  disabled={savingSection}
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-md text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
                >
                  <Trash2 className="w-4 h-4" /> Delete section
                </button>
              ) : (
                <p className={`text-xs ${subtle}`}>Remove all templates to delete this section.</p>
              ))}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setSectionModal(null)}
              disabled={savingSection}
              className={`px-4 py-2 rounded-md text-sm ${
                darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'
              }`}
            >
              Cancel
            </button>
            <button
              onClick={submitSection}
              disabled={savingSection}
              className={`px-4 py-2 rounded-md text-sm font-medium text-white ${accent.button} disabled:bg-gray-400`}
            >
              {savingSection ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Add template modal */}
      <Modal isOpen={!!itemModal} onClose={() => !savingItem && setItemModal(null)} maxWidth="md">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">Add template</h2>
        <p className={`text-sm ${subtle} mb-4`}>
          Adds a new template slot to “{activeSection?.label}”. Upload its first file afterward.
        </p>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Icon &amp; name
        </label>
        <div className="flex items-center gap-3 mb-4">
          <button
            type="button"
            onClick={() => setEmojiTarget({ kind: 'newItem' })}
            title="Choose an icon for this template"
            className={`shrink-0 w-11 h-11 rounded-lg flex items-center justify-center transition-colors ${
              darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
            }`}
          >
            {itemModal?.emoji ? (
              <span className="text-2xl leading-none">{itemModal.emoji}</span>
            ) : (
              <SmilePlus className={`w-5 h-5 ${subtle}`} />
            )}
          </button>
          <input
            type="text"
            value={itemModal?.name || ''}
            onChange={(e) => setItemModal((m) => ({ ...m, name: e.target.value }))}
            placeholder={itemPlaceholder}
            autoFocus
            className={`flex-1 px-3 py-2 border rounded-md ${
              darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
            }`}
          />
        </div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Description (optional)
        </label>
        <textarea
          value={itemModal?.description || ''}
          onChange={(e) => setItemModal((m) => ({ ...m, description: e.target.value }))}
          rows={2}
          placeholder="Short description shown on the card"
          className={`w-full px-3 py-2 mb-4 border rounded-md ${
            darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
          }`}
        />
        {itemError && (
          <p className="text-sm text-red-600 mb-3 flex items-center gap-1">
            <AlertTriangle className="w-4 h-4" /> {itemError}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button
            onClick={() => setItemModal(null)}
            disabled={savingItem}
            className={`px-4 py-2 rounded-md text-sm ${
              darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'
            }`}
          >
            Cancel
          </button>
          <button
            onClick={submitItem}
            disabled={savingItem}
            className={`px-4 py-2 rounded-md text-sm font-medium text-white ${accent.button} disabled:bg-gray-400`}
          >
            {savingItem ? 'Saving...' : 'Add template'}
          </button>
        </div>
      </Modal>

      {/* Canva link modal */}
      <Modal isOpen={!!canvaEntry} onClose={() => !canvaSaving && setCanvaEntry(null)} maxWidth="md">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">Canva link</h2>
        <p className={`text-sm ${subtle} mb-4`}>
          {canvaEntry?.doc?.title || canvaEntry?.name} — paste the Canva share link for this
          template. Clear the field to remove the link.
        </p>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Canva URL
        </label>
        <input
          type="url"
          value={canvaDraft}
          onChange={(e) => setCanvaDraft(e.target.value)}
          placeholder="https://www.canva.com/design/..."
          autoFocus
          className={`w-full px-3 py-2 mb-2 border rounded-md ${
            darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
          }`}
        />
        {canvaDraft.trim() && /^https?:\/\//i.test(canvaDraft.trim()) && !canvaDraft.includes('canva.com') && (
          <p className="text-xs text-amber-600 dark:text-amber-400 mb-2 flex items-center gap-1">
            <AlertTriangle className="w-3.5 h-3.5" /> This doesn't look like a canva.com link — double-check before saving.
          </p>
        )}
        {canvaError && (
          <p className="text-sm text-red-600 mb-3 flex items-center gap-1">
            <AlertTriangle className="w-4 h-4" /> {canvaError}
          </p>
        )}
        <div className="flex justify-end gap-2 mt-2">
          <button
            onClick={() => setCanvaEntry(null)}
            disabled={canvaSaving}
            className={`px-4 py-2 rounded-md text-sm ${
              darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'
            }`}
          >
            Cancel
          </button>
          <button
            onClick={submitCanva}
            disabled={canvaSaving}
            className="px-4 py-2 rounded-md text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-400"
          >
            {canvaSaving ? 'Saving...' : 'Save link'}
          </button>
        </div>
      </Modal>

      {/* Emoji icon picker (templates and sections; stacks above the section modal) */}
      <Modal isOpen={!!emojiTarget} onClose={() => setEmojiTarget(null)} maxWidth="xl" zIndex={100}>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-1 text-center">
          Choose an icon
        </h2>
        <p className={`text-sm ${subtle} mb-4 text-center`}>
          {emojiTarget?.kind === 'section'
            ? sectionModal?.label || 'New section'
            : emojiTarget?.kind === 'newItem'
            ? itemModal?.name || 'New template'
            : emojiTarget?.entry?.doc?.title || emojiTarget?.entry?.name || emojiTarget?.entry?.title}
        </p>
        <div className="flex justify-center">
          {emojiTarget && (
            <React.Suspense
              fallback={
                <div className="flex justify-center items-center h-72 w-full">
                  <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${accent.spinner}`} />
                </div>
              }
            >
              <EmojiPicker
                onEmojiClick={(emojiData) => pickEmoji(emojiData.emoji)}
                theme={darkMode ? 'dark' : 'light'}
                width="100%"
                height={380}
                lazyLoadEmojis
                previewConfig={{ showPreview: false }}
              />
            </React.Suspense>
          )}
        </div>
        <div className="flex items-center justify-between mt-4">
          {(emojiTarget?.kind === 'section'
            ? sectionModal?.emoji
            : emojiTarget?.kind === 'newItem'
            ? itemModal?.emoji
            : emojiTarget?.entry?.emoji) ? (
            <button
              onClick={() => pickEmoji('')}
              className="px-3 py-2 rounded-md text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
            >
              Use default icon
            </button>
          ) : (
            <span />
          )}
          <button
            onClick={() => setEmojiTarget(null)}
            className={`px-4 py-2 rounded-md text-sm ${
              darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'
            }`}
          >
            Cancel
          </button>
        </div>
      </Modal>

      <DocumentViewerModal
        isOpen={!!viewerDoc}
        onClose={() => setViewerDoc(null)}
        url={viewerDoc?.url}
        title={viewerDoc?.title}
      />
    </div>
  );
};

export default TemplateCollections;
